import React, { useState } from 'react';
import ReadOnlyCodeViewer from '../../../Utils/ReadOnlyCodeViewer';
import AudioPlayer from '../../../../Utils/AudioPlayer';
import VideoPlayer from '../../../../Utils/VideoPlayer';

const FollowupQuestionsAccordion = ({ followupQuestions, skillsTested }) => {
  const [collapsedSections, setCollapsedSections] = useState(
    followupQuestions.map(() => false) // All sections collapsed initially
  );

  const toggleCollapse = (index) => {
    setCollapsedSections(prev => {
      const newState = [...prev];
      newState[index] = !newState[index];
      return newState;
    });
  };

  // Function to render answers based on question type
  const renderAnswer = (answer, questionType, file_key) => {
    // For empty answers, show a message
    if (!answer) {
      return <p className="text-gray-500 italic">No answer provided</p>;
    }
    
    // Don't show placeholder answers
    if (answer === "[USER ANSWER APPENDED HERE]") return null;
    
    // Handle audio-answer type - primarily audio with optional text transcript
    if (questionType === 'audio-answer') {
      return (
        <>
          {answer && typeof answer === 'string' && (
            <p className="text-gray-800 bg-blue-50 p-3 rounded-md mb-2 whitespace-pre-wrap">
              {answer}
            </p>
          )}
          <AudioPlayer file_key={file_key} />
        </>
      );
    }
    
    // Define code question types that need syntax highlighting for their answers
    const codeWritingQuestionTypes = ['code-write', 'code-completion', 'code-refactoring'];
    
    // For multiple-answer questions, the answer might be an array
    if (questionType === 'multiple-answer' && Array.isArray(answer)) {
      return (
        <div className="flex flex-row flex-wrap gap-2">
          {answer.map((ans, index) => (
            <div key={index} className="px-4 py-2 bg-blue-100 rounded-full text-blue-700">
              {ans}
            </div>
          ))}
        </div>
      );
    } else if (questionType === 'single-answer' && typeof answer === 'string') {
      return (
        <div className="px-4 py-2 bg-blue-100 rounded-full text-blue-700 inline-block">
          {answer}
        </div>
      );
    } else if (questionType === 'true-false' && typeof answer === 'string') {
      return (
        <div className="px-4 py-2 bg-blue-100 rounded-full text-blue-700 inline-block">
          {answer}
        </div>
      );
    } else if (['long-text', 'short-text', 'code-explanation', 'find-the-bug', 'security-task', 'performance-optimization'].includes(questionType) && typeof answer === 'string') {
      return (
        <>
          <p className="text-gray-800 bg-blue-50 p-3 rounded-md whitespace-pre-wrap">
            {answer}
          </p>
          <AudioPlayer file_key={file_key} />
        </>
      );
    }
    // Handle code writing questions that need syntax highlighting for their answers
    else if (codeWritingQuestionTypes.includes(questionType)) {
      return (
        <ReadOnlyCodeViewer 
          code={answer || ''}
          skillsTested={skillsTested}
          maxHeight={400}
        />
      );
    }
    
    // Fallback for any other type of answer
    return (
      <p className="text-gray-800">
        {typeof answer === 'object' && answer !== null
          ? (answer.text 
              ? answer.text 
              : JSON.stringify(answer))
          : (answer !== null && answer !== undefined
              ? answer.toString()
              : 'No answer provided')}
      </p>
    );
  };

  if (!followupQuestions || followupQuestions.length === 0) {
    return null;
  }

  return (
    <div className="mt-4 border-t pt-3 border-gray-200">
      <h4 className="text-md font-medium text-gray-700 mb-2">Follow-up Questions and Answers:</h4>
      <div className="space-y-0">
        {followupQuestions.map((followup, index) => (
          <div key={index}>
            <div className="border-t border-gray-200">
              <div 
                className="cursor-pointer hover:bg-gray-50 transition-colors duration-200 group"
                onClick={() => toggleCollapse(index)}
              >
                <div className={`flex items-center justify-between w-full text-left py-4 transition-all duration-200 ${
                  !collapsedSections[index] ? 'px-4' : 'group-hover:px-4'
                }`}>
                  <div className="flex items-center">
                    <div className="bg-[#33C6B6] text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h4 className="text-sm text-gray-600">
                      <span className="font-bold">Follow-up Question {index + 1}</span> (Answered)
                    </h4>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                      collapsedSections[index] ? 'rotate-90' : 'rotate-0'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                {collapsedSections[index] && (
                  <div className="mt-3 ml-9 pb-4 px-4">
                    <p className="text-lg text-gray-700 mb-4">
                      <span className="font-bold">Q: </span>{followup.question_text}
                    </p>
                    
                    {/* Display code snippet if available */}
                    {followup.codeSnippet && (
                      <div className="mb-4">
                        <ReadOnlyCodeViewer 
                          code={followup.codeSnippet}
                          skillsTested={skillsTested}
                          maxHeight={400}
                        />
                      </div>
                    )}
                    
                    <div className="pl-4 border-l-4 border-blue-200 py-2">
                      <h4 className="text-md font-medium text-gray-700 mb-2">Answer:</h4>
                      {renderAnswer(followup.answer_text, followup.type, followup.file_key)}
                    </div>
                    
                    {/* Display reason if available */}
                    {followup.reason && (
                      <div className="mt-3 pl-4 border-l-4 border-green-200 py-2">
                        <h4 className="text-md font-medium text-gray-700 mb-2">Reason:</h4>
                        <p className="text-gray-800">{followup.reason}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FollowupQuestionsAccordion;
